import type { FittingUpgradeTarget } from "@frontier/content/ship-fitting";
import { useCallback, useRef, useState } from "react";
import type { RefObject } from "react";
import type { EquipmentKey } from "@frontier/content/arsenal";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { Profile } from "@frontier/protocol/contracts";
import { ArsenalOperations } from "./arsenal-operations";
import type { ArsenalCommand, ArsenalIntent } from "./arsenal-operations";
import { api, requestId } from "./session-api";
import type { ProfileSync } from "./profile-sync";
import { browserAuthStorage } from "./auth-storage";
import { useUpgradePrices } from "./use-upgrade-prices";

export function useArsenalActions(
  profile: Profile | null,
  token: RefObject<string>,
  profileSync: ProfileSync,
  setBusy: (busy: boolean) => void,
  setNotice: (message: string) => void,
  unauthorized: (message: string) => void,
) {
  const upgradePricing = useUpgradePrices();
  const [operations] = useState(
    () => new ArsenalOperations(browserAuthStorage(), requestId),
  );
  const [, refresh] = useState(0);
  const active = useRef(profile);
  active.current = profile;
  const execute = useCallback(
    async (command: ArsenalCommand | ArsenalIntent) => {
      const playerId = active.current?.playerId;
      if (!playerId) return;
      const ticket = profileSync.ticket();
      const sessionToken = token.current;
      const current = () =>
        profileSync.ticket() === ticket && token.current === sessionToken;
      setBusy(true);
      try {
        const intent =
          "requestId" in command
            ? command
            : operations.begin(playerId, command);
        refresh((version) => version + 1);
        const outcome = await operations.execute(
          playerId,
          sessionToken,
          intent,
          api,
          current,
        );
        if (current() && active.current?.playerId === playerId) {
          if (outcome.authExpired) {
            unauthorized(
              "Сессия истекла. Войди снова и проверь операцию.",
            );
            return;
          }
          let confirmedProfile: Profile | undefined;
          if (outcome.status === "confirmed" && outcome.profile) {
            if (profileSync.commit(outcome.profile, ticket)) confirmedProfile = outcome.profile;
          }
          else await profileSync.refresh(ticket);
          if (outcome.priceChanged) {
            const prices = await upgradePricing.reload();
            if (current())
              setNotice(
                prices
                  ? "Стоимость обновлена. Проверь цену и нажми улучшение ещё раз."
                  : "Не удалось обновить цену. Повтори загрузку в оснащении.",
              );
          } else setNotice(outcome.message);
          if (current()) return confirmedProfile;
        }
      } catch (error) {
        if (!current()) return;
        setNotice(
          error instanceof Error
            ? error.message
            : "Не удалось сохранить операцию арсенала",
        );
      } finally {
        if (current()) setBusy(false);
        refresh((version) => version + 1);
      }
    },
    [
      operations,
      token,
      profileSync,
      setBusy,
      setNotice,
      unauthorized,
      upgradePricing.reload,
    ],
  );
  return {
    upgradePricing,
    pendingArsenal: profile ? operations.pending(profile.playerId) : [],
    craftEquipment: useCallback(
      (key: EquipmentKey) => execute({ operation: "craft", key }),
      [execute],
    ),
    dismantleEquipment: useCallback(
      async (key: EquipmentKey, tier: EquipmentTier) => { await execute({ operation: "dismantle", key, tier }); },
      [execute],
    ),
    upgradeEquipment: useCallback(
      async (key: EquipmentKey, fromTier: EquipmentTier, mount?: FittingUpgradeTarget) => {
        if (!upgradePricing.prices) {
          setNotice("Сначала загрузи стоимость улучшений.");
          return;
        }
        return execute({
          operation: "upgrade",
          key,
          fromTier,
          ...(mount ? { mount } : {}),
          priceVersion: upgradePricing.prices.version,
        });
      },
      [execute, upgradePricing.prices, setNotice],
    ),
    retryArsenal: useCallback(
      async (intent: ArsenalIntent) => { await execute(intent); },
      [execute],
    ),
  };
}
